<template>
    <div class="person-entry-fssc22000-form">
        <hr />
        <h5 class="mb-3">Formulario FSSC 22000</h5>
        <p class="text-muted small mb-3">
            Certificación de inocuidad alimentaria (FSSC 22000 / GFSI). Todos los campos son obligatorios.
        </p>

        <div class="form-row">
            <div class="col-12">
                <app-form-text-input
                    label="Fondo de pensiones"
                    v-model="form.pension_fund"
                    :required="true"
                    :has-error="hasError('pension_fund')"
                    error-message="Debe indicar el fondo de pensiones"
                    @input="touch('pension_fund')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-form-text-input
                    label="Indique nombre de la EPS a la cual se encuentra afiliado(a)"
                    v-model="form.eps"
                    :required="true"
                    :has-error="hasError('eps')"
                    error-message="Debe indicar el nombre de la EPS"
                    @input="touch('eps')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12 col-md-6">
                <app-form-text-input
                    label="Nombre de contacto de emergencia"
                    v-model="form.emergency_contact_name"
                    :required="true"
                    :has-error="hasError('emergency_contact_name')"
                    error-message="Debe indicar el nombre del contacto de emergencia"
                    @input="touch('emergency_contact_name')"
                />
            </div>
            <div class="col-12 col-md-6">
                <app-form-text-input
                    label="Celular de contacto de emergencia"
                    v-model="form.emergency_contact_phone"
                    :required="true"
                    :has-error="hasError('emergency_contact_phone')"
                    error-message="Debe indicar el celular del contacto de emergencia"
                    @input="touch('emergency_contact_phone')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-boolean-field
                    label="¿Se siente en buenas condiciones de salud hoy?"
                    v-model="form.is_in_good_health_today"
                    :has-error="hasError('is_in_good_health_today')"
                    error-message="Debe responder si se siente en buenas condiciones de salud"
                    @input="touch('is_in_good_health_today')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-form-select-input
                    label="En los últimos 7 días, ¿ha presentado alguno de los siguientes síntomas?"
                    v-model="form.symptoms_last_7_days"
                    :options="symptomOptions"
                    field-key="value"
                    field-value="label"
                    :required="true"
                    :has-error="hasError('symptoms_last_7_days')"
                    error-message="Debe seleccionar un síntoma o la opción Ninguno"
                    @change="touch('symptoms_last_7_days')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-boolean-field
                    label="¿Presenta actualmente alguna herida, corte o lesión en manos, brazos o cara?"
                    v-model="form.has_current_wound_or_injury"
                    :has-error="hasError('has_current_wound_or_injury')"
                    error-message="Debe responder sobre heridas o lesiones actuales"
                    @input="touch('has_current_wound_or_injury')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-boolean-field
                    label="¿Está bajo tratamiento médico o consume medicamentos actualmente?"
                    v-model="form.is_under_medical_treatment"
                    :has-error="hasError('is_under_medical_treatment')"
                    error-message="Debe responder sobre tratamiento médico"
                    @input="touch('is_under_medical_treatment')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-boolean-field
                    label="¿Le han diagnosticado en el último mes alguna enfermedad infecciosa?"
                    v-model="form.diagnosed_with_infectious_disease_last_month"
                    :has-error="hasError('diagnosed_with_infectious_disease_last_month')"
                    error-message="Debe responder sobre enfermedades infecciosas"
                    @input="touch('diagnosed_with_infectious_disease_last_month')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-boolean-field
                    label="¿Presenta síntomas gastrointestinales?"
                    v-model="form.has_gastrointestinal_symptoms"
                    :has-error="hasError('has_gastrointestinal_symptoms')"
                    error-message="Debe responder sobre síntomas gastrointestinales"
                    @input="touch('has_gastrointestinal_symptoms')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-boolean-field
                    label="¿Ha tenido contacto reciente con personas enfermas o con diagnóstico infeccioso?"
                    v-model="form.had_recent_contact_with_infectious_person"
                    :has-error="hasError('had_recent_contact_with_infectious_person')"
                    error-message="Debe responder sobre contacto con personas enfermas"
                    @input="touch('had_recent_contact_with_infectious_person')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-boolean-field
                    label="¿Tiene heridas o lesiones sin protección que puedan entrar en contacto con los alimentos?"
                    v-model="form.has_unprotected_wounds_near_food"
                    :has-error="hasError('has_unprotected_wounds_near_food')"
                    error-message="Debe responder sobre heridas sin protección"
                    @input="touch('has_unprotected_wounds_near_food')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-boolean-field
                    label="¿Usa correctamente sus elementos de protección personal (EPP)?"
                    v-model="form.uses_ppe_correctly"
                    :has-error="hasError('uses_ppe_correctly')"
                    error-message="Debe responder sobre el uso de EPP"
                    @input="touch('uses_ppe_correctly')"
                />
            </div>
        </div>

        <div class="form-row">
            <div class="col-12">
                <app-form-select-input
                    label="¿Ha recibido capacitación en Manipulación de Alimentos y Buenas Prácticas Higiénicas?"
                    v-model="form.food_handling_training_status"
                    :options="trainingOptions"
                    field-key="value"
                    field-value="label"
                    :required="true"
                    :has-error="hasError('food_handling_training_status')"
                    error-message="Debe indicar el estado de la capacitación"
                    @change="touch('food_handling_training_status')"
                />
            </div>
        </div>
    </div>
</template>

<script>
import FormTextInput from '../../../../UI/Controls/Forms/FormTextInput.vue'
import FormSelectInput from '../../../../UI/Controls/Forms/FormSelectInput.vue'
import BooleanField from './Fssc22000BooleanField.vue'
import { symptomOptions, trainingOptions } from '../../../../../utils/fssc22000Labels'

export { symptomOptions, trainingOptions } from '../../../../../utils/fssc22000Labels'

export const emptyFssc22000Form = () => ({
    pension_fund: '',
    eps: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    is_in_good_health_today: '',
    symptoms_last_7_days: '',
    has_current_wound_or_injury: '',
    is_under_medical_treatment: '',
    diagnosed_with_infectious_disease_last_month: '',
    has_gastrointestinal_symptoms: '',
    had_recent_contact_with_infectious_person: '',
    has_unprotected_wounds_near_food: '',
    uses_ppe_correctly: '',
    food_handling_training_status: '',
})

export default {
    components: {
        appFormTextInput: FormTextInput,
        appFormSelectInput: FormSelectInput,
        appBooleanField: BooleanField,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        validation: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            symptomOptions,
            trainingOptions,
        }
    },
    computed: {
        form() {
            return this.value
        },
    },
    methods: {
        hasError(field) {
            return !!(this.validation && this.validation[field] && this.validation[field].$error)
        },
        touch(field) {
            if (this.validation && this.validation[field]) {
                this.validation[field].$touch()
            }
        },
        reset() {
            this.$emit('input', emptyFssc22000Form())
        },
    },
}
</script>
